import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseQueryResult } from '@tanstack/react-query';
import { Button, Card, Chip, EmptyState, Input, NumberInput, PageLoading, Select } from '@koyomi/ui';
import { api, ApiError } from '../../lib/api.ts';
import { useAdminSave } from '../../lib/queries.ts';
import { bandValid, getBand, i18nPath, setBand, THRESHOLD_ROWS } from './infra-ui.tsx';
import type { CurrentPlans, InfraSettings, PlanKind, RenderPlan, Thresholds } from './types.ts';

const KINDS: PlanKind[] = ['web', 'postgres', 'keyvalue'];
const CURRENT: { key: keyof CurrentPlans; kind: PlanKind }[] = [{ key: 'api', kind: 'web' }, { key: 'postgres', kind: 'postgres' }, { key: 'keyValue', kind: 'keyvalue' }];

/** Aba "Limites e planos": limites amarelo/vermelho, catálogo de planos do Render e planos atuais. Salva por seção. */
export function InfraSettingsPanel({ query }: { query: UseQueryResult<InfraSettings> }) {
  const { t } = useTranslation(['admin', 'common']);
  if (query.isLoading) return <PageLoading cards={3} rows={6} />;
  if (!query.data) return <EmptyState title={t('admin:infra.loadError')} description={query.error instanceof ApiError ? query.error.message : undefined} action={<Button onClick={() => void query.refetch()}>{t('admin:infra.retry')}</Button>} />;
  return (
    <div className="k-stack">
      <ThresholdsCard server={query.data.thresholds} />
      <CurrentPlansCard server={query.data.currentPlans} catalog={query.data.planCatalog} />
      <CatalogCard server={query.data.planCatalog} />
    </div>
  );
}

const useSettingsSave = () => useAdminSave((body: Partial<InfraSettings>) => api<InfraSettings>('/admin/infra/settings', { method: 'PUT', body }), ['/admin/infra']);

function ThresholdsCard({ server }: { server: Thresholds }) {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const [draft, setDraft] = useState<Thresholds | null>(null);
  const save = useSettingsSave();
  const th = draft ?? server;
  const invalid = THRESHOLD_ROWS.filter((r) => { const b = getBand(th, r.path); return !b || !bandValid(r.path, b); }).map((r) => r.path);
  const update = (path: string, which: 'yellow' | 'red', v: number | null) => {
    const cur = getBand(th, path) ?? { yellow: 0, red: 0 };
    setDraft(setBand(th, path, { ...cur, [which]: v ?? Number.NaN }));
  };
  return (
    <Card title={t('admin:infra.settings.thresholds')} subtitle={t('admin:infra.settings.thresholdsHint')}
      actions={draft && <div className="k-row" style={{ gap: 8, flexWrap: 'wrap' }}><Button size="sm" variant="ghost" onClick={() => setDraft(null)}>{t('admin:infra.settings.discard')}</Button><Button size="sm" variant="primary" loading={save.isPending} disabled={invalid.length > 0} onClick={() => save.mutate({ thresholds: th }, { onSuccess: () => setDraft(null) })}>{t('common:actions.save')}</Button></div>}>
      <div className="k-infra-thresholds">
        {THRESHOLD_ROWS.map((r) => {
          const b = getBand(th, r.path);
          const bad = invalid.includes(r.path);
          const suffix = r.unit;
          const rowLabel = t(`admin:infra.settings.labels.${i18nPath(r.path)}`, { defaultValue: r.path });
          return (
            <div key={r.path} className="k-infra-threshold">
              <div className="k-infra-threshold__label">
                <span>{rowLabel}</span>
                {r.lowerWorse && <Chip tone="outline">{t('admin:infra.settings.lowerWorse')}</Chip>}
                {bad && <div className="k-field__error">{t(r.lowerWorse ? 'admin:infra.settings.invalidLower' : 'admin:infra.settings.invalidHigher')}</div>}
              </div>
              <NumberInput aria-label={`${rowLabel} · ${t('admin:infra.settings.yellow')}`} label={t('admin:infra.settings.yellow')} locale={i18n.language} suffix={suffix} maxDecimals={2} min={0} value={b && Number.isFinite(b.yellow) ? b.yellow : null} onChange={(v) => update(r.path, 'yellow', v)} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
              <NumberInput aria-label={`${rowLabel} · ${t('admin:infra.settings.red')}`} label={t('admin:infra.settings.red')} locale={i18n.language} suffix={suffix} maxDecimals={2} min={0} value={b && Number.isFinite(b.red) ? b.red : null} onChange={(v) => update(r.path, 'red', v)} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function CurrentPlansCard({ server, catalog }: { server: CurrentPlans; catalog: RenderPlan[] }) {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const [draft, setDraft] = useState<CurrentPlans | null>(null);
  const save = useSettingsSave();
  const cur = draft ?? server;
  const usd = (v: number) => new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(v);
  return (
    <Card title={t('admin:infra.settings.currentPlans')} subtitle={t('admin:infra.settings.currentPlansHint')}
      actions={draft && <div className="k-row" style={{ gap: 8, flexWrap: 'wrap' }}><Button size="sm" variant="ghost" onClick={() => setDraft(null)}>{t('admin:infra.settings.discard')}</Button><Button size="sm" variant="primary" loading={save.isPending} onClick={() => save.mutate({ currentPlans: cur }, { onSuccess: () => setDraft(null) })}>{t('common:actions.save')}</Button></div>}>
      <div className="k-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))' }}>
        {CURRENT.map(({ key, kind }) => {
          const options = catalog.filter((p) => p.kind === kind);
          const known = options.some((p) => p.plan === cur[key]);
          return (
            <Select key={key} label={t(`admin:infra.settings.current.${key}`)} value={cur[key]} onChange={(e) => setDraft({ ...cur, [key]: e.target.value })}>
              {!known && <option value={cur[key]}>{cur[key] || '—'}</option>}
              {options.map((p) => <option key={p.plan} value={p.plan}>{p.plan} · {usd(p.priceUsd)}</option>)}
            </Select>
          );
        })}
      </div>
    </Card>
  );
}

type DraftPlan = { kind: PlanKind; plan: string; priceUsd: number | null; memoryMb: number | null; cpu: number | null; maxConnections: number | null };
const toDraft = (p: RenderPlan): DraftPlan => ({ kind: p.kind, plan: p.plan, priceUsd: p.priceUsd, memoryMb: p.memoryMb, cpu: p.cpu ?? null, maxConnections: p.maxConnections ?? null });

function CatalogCard({ server }: { server: RenderPlan[] }) {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const [draft, setDraft] = useState<DraftPlan[] | null>(null);
  const save = useSettingsSave();
  const rows = draft ?? server.map(toDraft);
  const keyOf = (p: DraftPlan) => `${p.kind}:${p.plan.trim().toLowerCase()}`;
  const errorOf = (p: DraftPlan, i: number): string | null => {
    if (!p.plan.trim()) return t('admin:infra.settings.catalog.errPlan');
    if (p.priceUsd == null || p.priceUsd < 0) return t('admin:infra.settings.catalog.errPrice');
    if (p.memoryMb == null || p.memoryMb <= 0) return t('admin:infra.settings.catalog.errMemory');
    if (rows.findIndex((o) => keyOf(o) === keyOf(p)) !== i) return t('admin:infra.settings.catalog.errDuplicate');
    return null;
  };
  const errors = rows.map(errorOf);
  const hasErrors = errors.some(Boolean);
  const set = (i: number, patch: Partial<DraftPlan>) => setDraft(rows.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const submit = () => save.mutate({ planCatalog: rows.map((r) => ({ kind: r.kind, plan: r.plan.trim(), priceUsd: r.priceUsd ?? 0, memoryMb: r.memoryMb ?? 0, ...(r.cpu != null ? { cpu: r.cpu } : {}), ...(r.maxConnections != null ? { maxConnections: r.maxConnections } : {}) })) }, { onSuccess: () => setDraft(null) });
  const lang = i18n.language;
  return (
    <Card title={t('admin:infra.settings.catalog.title')} subtitle={<>{t('admin:infra.settings.catalog.pricesApprox')} <a href="https://render.com/pricing" target="_blank" rel="noreferrer">render.com/pricing</a></>}
      actions={draft && <div className="k-row" style={{ gap: 8, flexWrap: 'wrap' }}><Button size="sm" variant="ghost" onClick={() => setDraft(null)}>{t('admin:infra.settings.discard')}</Button><Button size="sm" variant="primary" loading={save.isPending} disabled={hasErrors} onClick={submit}>{t('common:actions.save')}</Button></div>}>
      <div className="k-stack" style={{ gap: 10 }}>
        {rows.map((p, i) => (
          <div key={i} className="k-infra-plan">
            <div className="k-infra-plan__fields">
              <Select label={t('admin:infra.settings.catalog.kind')} value={p.kind} onChange={(e) => set(i, { kind: e.target.value as PlanKind })}>{KINDS.map((k) => <option key={k} value={k}>{t(`admin:infra.settings.catalog.kinds.${k}`)}</option>)}</Select>
              <Input label={t('admin:infra.settings.catalog.plan')} value={p.plan} onChange={(e) => set(i, { plan: e.target.value })} />
              <NumberInput label={`${t('admin:infra.settings.catalog.price')} (USD)`} prefix="$" locale={lang} decimals={0} maxDecimals={2} min={0} value={p.priceUsd} onChange={(v) => set(i, { priceUsd: v })} />
              <NumberInput label={t('admin:infra.settings.catalog.memory')} suffix="MB" locale={lang} min={0} value={p.memoryMb} onChange={(v) => set(i, { memoryMb: v })} />
              <NumberInput label="CPU" locale={lang} maxDecimals={2} min={0} value={p.cpu} onChange={(v) => set(i, { cpu: v })} />
              <NumberInput label={t('admin:infra.settings.catalog.connections')} locale={lang} min={0} value={p.maxConnections} onChange={(v) => set(i, { maxConnections: v })} />
            </div>
            <div className="k-row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <span className="k-field__error">{errors[i] ?? ''}</span>
              <Button size="sm" variant="danger" onClick={() => setDraft(rows.filter((_, k) => k !== i))}>{t('admin:infra.settings.catalog.remove')}</Button>
            </div>
          </div>
        ))}
        <div><Button size="sm" onClick={() => setDraft([...rows, { kind: 'web', plan: '', priceUsd: null, memoryMb: null, cpu: null, maxConnections: null }])}>+ {t('admin:infra.settings.catalog.add')}</Button></div>
      </div>
    </Card>
  );
}
